import React, { useMemo, useCallback, createContext, useContext } from 'react';
import {
  Autocomplete,
  FormControl,
  Checkbox,
  TextField,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ButtonV2 } from 'components/common/ui';

import styles from './multiSelect.style';

// Context to pass data to the stable PaperComponent
const MultiSelectContext = createContext({});

// Stable PaperComponent defined outside to prevent re-creation on every render
const CustomPaper = ({ children, ...paperProps }) => {
  const { value, options, hasSelectAllOption, onChange, handleOnChange } =
    useContext(MultiSelectContext);

  return (
    <Paper
      sx={styles.paper}
      onMouseDown={(e) => e.preventDefault()}
      {...paperProps}
    >
      {!hasSelectAllOption ? (
        <>
          {value?.length > 0 && (
            <>
              <Box
                onMouseDown={(e) => e.preventDefault()}
                sx={styles.headerWrapper}
              >
                <Box sx={{ flex: 1 }} />
                <ButtonV2
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOnChange(null, []);
                  }}
                  sx={styles.resetButton}
                >
                  Reset
                </ButtonV2>
              </Box>
              <Divider />
            </>
          )}
          {children}
        </>
      ) : (
        <>
          <Box
            onMouseDown={(e) => e.preventDefault()}
            sx={styles.headerWrapper}
          >
            <ButtonV2
              variant="text"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(value?.length === options?.length ? [] : options);
              }}
              sx={styles.selectAllButton}
            >
              {value?.length === options?.length
                ? 'Deselect all'
                : 'Select all'}
            </ButtonV2>
            {value?.length > 0 && (
              <ButtonV2
                variant="text"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOnChange(null, []);
                }}
                sx={styles.resetButton}
              >
                Reset
              </ButtonV2>
            )}
          </Box>
          <Divider />
          {children}
        </>
      )}
    </Paper>
  );
};

const MultiSelect = ({
  value,
  onChange,
  type,
  loading = false,
  label = '',
  options,
  variant = 'outlined', // standard, outlined, filled
  error,
  name,
  minWidth,
  hasSelectAllOption = false,
  keepScrollPosition = false, // When true, dropdown won't scroll to top on selection
  ...props
}) => {
  const handleOnChange = useCallback(
    (_event, newValue, reason) => {
      if (onChange) {
        onChange(newValue, reason);
      }
    },
    [onChange]
  );

  const icon = (
    <CheckBoxOutlineBlankIcon
      data-testid="CheckBoxOutlineBlankIcon"
      fontSize="small"
    />
  );

  const checkedIcon = (
    <CheckBoxIcon data-testid="CheckBoxIcon" fontSize="small" />
  );
  const hasValue = !!value?.length;

  const renderOption = (autoCompleteProps, option, { selected }) => {
    const { key, ...restAutoCompleteProps } = autoCompleteProps;

    return (
      <Box
        component="li"
        {...restAutoCompleteProps}
        sx={styles.filterOptionItem}
        key={`${option.value}-${key}`}
      >
        <Checkbox
          key={`checkbox-${key}`}
          size="small"
          icon={icon}
          checkedIcon={checkedIcon}
          sx={styles.checkbox}
          checked={selected}
        />
        {option.text || ''}
      </Box>
    );
  };
  const renderInput = (params) => {
    return (
      <TextField
        variant="outlined"
        placeholder={hasValue ? '' : label}
        error={!!error}
        sx={{ ...styles.textfield(variant) }}
        data-testid={`input-${name}`}
        {...params}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <Box sx={styles.endAdornmentWrapper}>
              {params.InputProps?.endAdornment}
            </Box>
          ),
        }}
      />
    );
  };

  const contextValue = useMemo(
    () =>
      keepScrollPosition
        ? {
            value,
            options,
            hasSelectAllOption,
            onChange,
            handleOnChange,
          }
        : undefined,
    [
      keepScrollPosition,
      value,
      options,
      hasSelectAllOption,
      onChange,
      handleOnChange,
    ]
  );

  const autocomplete = (
    <Autocomplete
      multiple
      disablePortal={!keepScrollPosition}
      disableCloseOnSelect
      popupIcon={
        <ArrowDropDownIcon
          data-testid="ArrowDropDownIcon"
          color="dropdownArrow"
          sx={styles.icon}
        />
      }
      {...(keepScrollPosition && { disableListWrap: true })}
      limitTags={2}
      sx={styles.autocomplete}
      disabled={loading}
      value={value || []}
      onChange={handleOnChange}
      loading={loading}
      isOptionEqualToValue={(option, newValue) => {
        return option.value === newValue?.value;
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string' || option instanceof String) {
          return option;
        }
        return option?.text || option?.label;
      }}
      options={options}
      {...(keepScrollPosition && { PaperComponent: CustomPaper })}
      renderOption={renderOption}
      renderInput={renderInput}
      size="small"
      slotProps={{
        listbox: { style: { maxHeight: 300 } },
        ...(keepScrollPosition && {
          popper: { style: { zIndex: 1400 } },
        }),
      }}
      {...props}
    />
  );

  const formControl = (
    <FormControl variant={variant} sx={{ m: 0, width: '100%' }}>
      <Box sx={styles.wrapper(minWidth)}>{autocomplete}</Box>
    </FormControl>
  );

  // Only wrap with context provider when keepScrollPosition is true
  if (keepScrollPosition) {
    return (
      <MultiSelectContext.Provider value={contextValue}>
        {formControl}
      </MultiSelectContext.Provider>
    );
  }

  return formControl;
};

export default MultiSelect;
